import { isNative as isNativeFromJWeb } from '../../services/JWeb';
import {
  getContainers,
  getRepositories,
  getServices
} from '../../infra/commonInitializer';
import SubscriberRestartTenantServices from './subscriberRestartTenantServices';
import subscriberExchangeTokenToUpdateValueStore from './subscriberTokenToValueStore';
import SetFirstApplicationToken from './setFirstApplicationToken';
import SetSupportSessionToken from './setSupportSessionToken';
import CleanUpEnvironment from './cleanUpEnvironment';
import UsePostRedirectService from './usePostRedirect';
import ServiceWorkerRules from './ServiceWorkerRules';
import EventsBehaviors from './EventsBehaviors';
import RemoveMaxAgeCookieInLoggedIn from './removeMaxAgeCookieInLoggedIn';
import {
  RefreshWhenNativeAuthStatusChanged,
  initialNavigationStateRepository
} from './RefreshWhenNativeAuthStatusChanged';
import { MultipleTabsDeamon } from './MultipleTabsDeamon';
import SetFirstTenantForNative from './SetFirstTenantForNative';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { container } from 'tsyringe';
import { ShellFlagsInputType } from '../../infra/commonInitializer/types';
import ForceReloadAfterUserOnboardingPath from './forceReloadAfterUserOnboardingPath';
import NavigationElementsBeforeInitBehavior from './NavigationElementsBeforeInitBehavior';
import { ServicePriority } from '../../containers/IContainer';
import RenewTenantHandlerKey from './renewTenantHandlerKey';

export default class BeforeInitBehaviors {
  public static async init(): Promise<void> {
    const isNative = await isNativeFromJWeb();

    const repositories = getRepositories();
    const { lastTenantIdsRepository } = repositories;

    const services = getServices();
    const {
      accessControlContainer,
      navigationService,
      sessionService,
      authTokenService,
      supportSessionService,
      tenantHandlerService,
      applicationService,
      stateParamHandlerService,
      routesService,
      serviceWorkerService,
      eventService,
      userOnboardingService
    } = services;

    const { authRefreshContainer } = getContainers();

    const removeMaxAgeCookieInLoggedIn = new RemoveMaxAgeCookieInLoggedIn();

    const shellFlagsProps =
      container.resolve<ShellFlagsInputType>('ShellFlagsProps');

    const web_setFirstApplicationToken =
      !isNative &&
      new SetFirstApplicationToken({
        sessionService,
        authTokenService,
        supportSessionService,
        shouldRefreshToken: shellFlagsProps?.enableTokenRefreshOnInit
      });

    const web_setSupportSessionToken =
      !isNative &&
      new SetSupportSessionToken({ supportSessionService, authTokenService });

    const subscriberRestartTenantServices = new SubscriberRestartTenantServices(
      {
        accessControlContainer
      }
    );

    const web_writeCookieDataToValueStore =
      !isNative && new subscriberExchangeTokenToUpdateValueStore();

    const cleanUpEnvironment = new CleanUpEnvironment({
      sessionService
    });

    const usePostRedirectService = new UsePostRedirectService({
      stateParamHandlerService
    });

    const events = new EventsBehaviors({
      eventService,
      navigationService
    });

    const serviceWorkerRules = new ServiceWorkerRules({
      serviceWorkerService,
      sessionService
    });

    const native_setFirstTenantForNative =
      isNative &&
      new SetFirstTenantForNative({
        authTokenService,
        tenantHandlerService,
        applicationService
      });

    const native_refreshWhenNativeAuthStatusChanged =
      isNative &&
      new RefreshWhenNativeAuthStatusChanged({
        authRefreshContainer,
        refreshScreenOnChange: shellFlagsProps?.nativeRefreshListener,
        nativeRefreshServicesOnAuthChange:
          shellFlagsProps?.nativeRefreshServicesOnAuthChange,
        initialNavigationStateRepository,
        eventService,
        navigationService,
        tenantHandlerService,
        sessionService
      });

    //TODO: Currently, the internalLogger is not working here. It should be investigated.
    /* istanbul ignore next */
    internalLogger.debug?.(
      `Manifest flag "nativeRefreshListener" status is ${
        shellFlagsProps?.nativeRefreshListener
      }, RefreshWhenNativeAuthStatusChanged is ${
        native_refreshWhenNativeAuthStatusChanged
          ? 'instantiated'
          : 'not instantiated'
      } `
    );

    // A provisory place to the deamon for multitab
    // This also responsible to SetTenant for the first time (On Native)
    const multipleTabsDeamon = new MultipleTabsDeamon({
      repository: lastTenantIdsRepository,
      isNative: isNative
    });

    const forceReloadAfterUserOnboardingPath =
      new ForceReloadAfterUserOnboardingPath({
        navigation: navigationService,
        userOnboardingService
      });

    if (isNative) {
      const native_renewTenantHandlerKey = new RenewTenantHandlerKey({
        routesService,
        navigationService,
        tenantHandlerService
      });
      authRefreshContainer.addService(sessionService, ServicePriority.CRITICAL);

      // TODO: maybe this is won't be needed in the future
      authRefreshContainer.addService(
        native_renewTenantHandlerKey,
        ServicePriority.HIGH
      );
    }

    /**
     *  Execution phase
     */
    internalLogger?.debug?.('Starting ClientOS Behaviors');

    // 1. Cleaners
    removeMaxAgeCookieInLoggedIn?.init?.();
    cleanUpEnvironment?.init?.();

    // 2. Setters
    multipleTabsDeamon.init();

    forceReloadAfterUserOnboardingPath.init();
    await subscriberRestartTenantServices?.init?.();
    await web_setFirstApplicationToken?.init?.();
    await web_setSupportSessionToken?.init?.();
    await web_writeCookieDataToValueStore?.init?.();
    await native_setFirstTenantForNative?.init?.();

    usePostRedirectService.init?.();
    serviceWorkerRules?.init?.();
    events?.init?.();
    await native_refreshWhenNativeAuthStatusChanged?.init?.();
    internalLogger?.debug?.('ClientOS Behavior initialized.');

    new NavigationElementsBeforeInitBehavior()?.init?.();
  }
}
